import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "reviewer73-theme";
const listeners = new Set<() => void>();

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStored(): Theme | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" ? raw : null;
}

let current: Theme = readStored() ?? systemTheme();

function apply(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
}
apply(current);

window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  if (readStored()) return;
  current = systemTheme();
  apply(current);
  listeners.forEach((l) => l());
});

function setTheme(next: Theme) {
  localStorage.setItem(STORAGE_KEY, next);
  current = next;
  apply(current);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Persisted light/dark toggle shared across every caller — a single external
 * store, not one useState per component, so every consumer (the header
 * toggle, the canvas charts reading CSS vars) re-renders on the same flip. */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, () => current);
  return [theme, setTheme];
}
